import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { z } from 'zod';
import { Navigate, useNavigate, useSearchParams } from 'react-router';
import { ArrowLeft, ArrowRight, Building2, Eye, EyeOff, FileCheck2, GraduationCap, Landmark, ListChecks, ShieldCheck, Users } from 'lucide-react';
import type { TenantChoice } from '@/api/identity';
import { useAuth, useSessionStatus } from '@/auth/useAuth';
import { Button, IconButton } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { Input, Radio } from '@/components/ui/Input';
import { Banner } from '@/components/ui/Feedback';
import { ApiError, errorMessage } from '@/lib/http';
import { applyFieldErrors } from '@/lib/forms';
import { cn } from '@/lib/cn';
import { BrandMark, usePublicBrand, useBrandFavicon, type Brand } from '@/features/branding/brand';
import { IDENTIFIER_MAX, loginSchema } from './schemas';

type FormInput = z.input<typeof loginSchema>;
type FormValues = z.output<typeof loginSchema>;

/** `?tenant=CODE` (e.g. from a tenant's welcome email) pre-selects the tenant; anything else is ignored. */
function tenantParam(v: string | null): string | undefined {
  const code = v?.trim().toUpperCase();
  return code && /^[A-Z0-9]{2,20}$/.test(code) ? code : undefined;
}

/** Only same-origin relative paths are allowed as redirect targets. */
function safeNext(next: string | null): string {
  if (!next || !next.startsWith('/') || next.startsWith('//')) return '/';
  return next;
}

/** Staggered entrance (opacity + translate only, see DESIGN.md §7). */
const rise = (i: number): CSSProperties => ({
  animationDelay: `${80 + i * 70}ms`,
});

const STAGES = [
  {
    title: 'Báo giá & phê duyệt',
    text: 'Chính sách duyệt theo giá trị, chiết khấu',
    icon: FileCheck2,
  },
  {
    title: 'Hợp đồng & lớp học',
    text: 'Bàn giao vận hành, điểm danh, nghiệm thu',
    icon: GraduationCap,
  },
  {
    title: 'Thu tiền & hoa hồng',
    text: 'Đối chiếu sao kê trước khi ghi nhận',
    icon: Landmark,
  },
] as const;

/** Tenant hosts: nothing that advertises modules the company may not use. */
const NEUTRAL_STAGES = [
  { title: 'Khách hàng', text: 'Thông tin và lịch sử làm việc tập trung', icon: Users },
  { title: 'Công việc', text: 'Mỗi bước có người phụ trách', icon: ListChecks },
  { title: 'Bảo mật', text: 'Phân quyền theo vai trò, có nhật ký', icon: ShieldCheck },
] as const;

export default function LoginPage() {
  // Until the host's branding is known the brand texts stay invisible (no Nguyên Bình flash on a company's domain).
  const { brand, loading: brandLoading } = usePublicBrand();
  useBrandFavicon(brand.logoUrl);
  useEffect(() => {
    if (!brandLoading) document.title = brand.custom ? `Đăng nhập · ${brand.name}` : 'CRM Nguyên Bình';
  }, [brandLoading, brand.custom, brand.name]);

  const { login } = useAuth();
  const status = useSessionStatus();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState<{
    message: string;
    traceId?: string;
  } | null>(null);
  // Set when the server answers 409 TENANT_SELECTION_REQUIRED: the account exists in several companies.
  const [choices, setChoices] = useState<TenantChoice[] | null>(null);
  const [picked, setPicked] = useState<string>('');
  const presetTenant = tenantParam(params.get('tenant'));
  const form = useForm<FormInput, unknown, FormValues>({
    resolver: zodResolver(loginSchema),
    mode: 'onTouched',
    defaultValues: { identifier: '', password: '' },
  });
  const next = safeNext(params.get('next'));

  if (status === 'authenticated') return <Navigate to={next} replace />;

  const attempt = async (values: FormValues, tenantCode?: string) => {
    setError(null);
    try {
      await login(tenantCode ? { ...values, tenantCode } : values);
      navigate(next, { replace: true });
    } catch (e) {
      if (e instanceof ApiError && e.errorCode === 'TENANT_SELECTION_REQUIRED' && e.body.tenants?.length) {
        setChoices(e.body.tenants);
        setPicked(e.body.tenants[0]!.code);
        return;
      }
      const { applied } = applyFieldErrors(e, form.setError);
      if (!applied)
        setError({
          message: errorMessage(e),
          traceId: e instanceof ApiError ? e.supportTraceId : undefined,
        });
    }
  };

  const onSubmit = form.handleSubmit((values) => attempt(values, choices ? picked : presetTenant));
  const back = () => {
    setChoices(null);
    setError(null);
  };

  const { errors, isSubmitting } = form.formState;

  return (
    <div className="relative grid min-h-[100dvh] overflow-hidden bg-bg lg:grid-cols-[minmax(0,1.15fr)_minmax(440px,1fr)]">
      {/* ---------------- Photo column ---------------- */}
      {/* Photo: Pexels #7845344 (RDNE Stock project), Pexels License — free for commercial use, self-hosted. */}
      <section className="relative hidden min-h-[100dvh] overflow-hidden bg-[#0f2a30] lg:block">
        <img
          src="/login/hero.jpg"
          alt=""
          aria-hidden
          className="anim-fade-in absolute inset-0 size-full object-cover object-[58%_center]"
          draggable={false}
        />
        {/* Ledger Teal tint, heavier toward the bottom where the copy sits. */}
        <div
          aria-hidden
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(180deg, rgba(15,42,48,0.55) 0%, rgba(15,42,48,0.35) 30%, rgba(15,42,48,0.82) 62%, rgba(13,36,41,0.96) 100%)',
          }}
        />

        <div
          className={cn(
            'relative flex h-full min-h-[100dvh] flex-col justify-between p-12 text-white xl:p-16',
            brandLoading && 'invisible',
          )}
        >
          <div className="flex items-center gap-3">
            <BrandMark brand={brand} onDark className="size-10 rounded-xl text-[13px]" />
            <div className="min-w-0 leading-tight">
              <p className="text-[15px] font-semibold">{brand.name}</p>
              <p className="text-xs text-white/65">{brand.custom ? (brand.tagline ?? brand.companyName) : brand.companyName}</p>
            </div>
          </div>

          <div className="max-w-[620px]">
            <p className="anim-rise text-xs font-semibold tracking-[0.14em] text-[#9fd3dc] uppercase" style={rise(0)}>
              {brand.custom ? 'Hệ thống quản trị nội bộ' : <>Nền tảng quản trị đào tạo &amp; dịch vụ</>}
            </p>
            <h1
              className="anim-rise mt-4 text-[clamp(2.1rem,3.3vw,3.25rem)] leading-[1.12] font-semibold tracking-[-0.02em]"
              style={rise(1)}
            >
              {brand.custom ? (
                <>
                  Làm việc cùng nhau,
                  <br />
                  <span className="text-[#9fd3dc]">trên một hệ thống.</span>
                </>
              ) : (
                <>
                  Quản trị trọn vòng đời
                  <br />
                  <span className="text-[#9fd3dc]">hợp đồng đào tạo.</span>
                </>
              )}
            </h1>
            <p className="anim-rise mt-5 max-w-[50ch] text-[15px] leading-relaxed text-white/75" style={rise(2)}>
              {brand.custom
                ? `Khách hàng, công việc và số liệu của ${brand.companyName} ở cùng một nơi — mỗi bước đều có người chịu trách nhiệm và nhật ký.`
                : 'Từ báo giá, phê duyệt nhiều cấp, ký hợp đồng đến mở lớp, thu tiền và chi hoa hồng — mỗi bước đều có người chịu trách nhiệm và nhật ký.'}
            </p>

            <ol className="anim-rise mt-10 grid grid-cols-3 border-t border-white/15 pt-6" style={rise(3)}>
              {(brand.custom ? NEUTRAL_STAGES : STAGES).map((s, i) => {
                const Icon = s.icon;
                return (
                  <li key={s.title} className={i > 0 ? 'border-l border-white/15 pl-5' : 'pr-5'}>
                    <span className="flex items-center gap-2 text-[#9fd3dc]">
                      <Icon className="size-4" aria-hidden />
                      <span className="font-mono text-[11px] text-white/45">0{i + 1}</span>
                    </span>
                    <p className="mt-2 text-sm font-medium">{s.title}</p>
                    <p className="mt-0.5 text-xs leading-snug text-white/55">{s.text}</p>
                  </li>
                );
              })}
            </ol>
          </div>
        </div>
      </section>

      {/* ---------------- Form column ---------------- */}
      <main className="relative flex flex-col bg-surface px-5 py-8 sm:px-10 lg:border-l lg:border-line lg:px-14 lg:py-10">
        <div className={cn('lg:hidden', brandLoading && 'invisible')}>
          <MobileBrand brand={brand} />
        </div>

        <div className="flex flex-1 items-center py-12">
          <form onSubmit={onSubmit} noValidate aria-labelledby="login-title" className="mx-auto w-full max-w-[380px]">
            <h2
              id="login-title"
              className="anim-rise text-[26px] leading-tight font-semibold tracking-[-0.02em] text-[#27272a]"
              style={rise(1)}
            >
              Đăng nhập
            </h2>
            <p className="anim-rise mt-2 text-sm text-muted" style={rise(2)}>
              Nhập tài khoản do quản trị hệ thống cấp.
              {presetTenant && !choices && (
                <span className="mt-1 block text-[13px] text-subtle">
                  Doanh nghiệp <span className="font-mono font-medium text-ink">{presetTenant}</span>
                </span>
              )}
            </p>

            <div className="anim-rise mt-8 flex flex-col gap-5" style={rise(3)}>
              {error && (
                <Banner tone="danger">
                  {error.message}
                  {error.traceId && <span className="mt-1 block font-mono text-2xs text-subtle">Mã hỗ trợ: {error.traceId}</span>}
                </Banner>
              )}

              {choices ? (
                <TenantPicker
                  identifier={form.getValues('identifier').trim()}
                  choices={choices}
                  value={picked}
                  onChange={setPicked}
                  onBack={back}
                  busy={isSubmitting}
                />
              ) : (
                <>
                  <Field label="Email hoặc số điện thoại" required error={errors.identifier?.message}>
                    {/* type="text" (not email/tel) so both are accepted; "username" lets password managers fill it. */}
                    <Input
                      className="h-12 text-[15px]"
                      type="text"
                      autoComplete="username"
                      inputMode="email"
                      placeholder="ten@congty.vn hoặc 0912 345 678"
                      autoCapitalize="none"
                      autoCorrect="off"
                      spellCheck={false}
                      enterKeyHint="next"
                      maxLength={IDENTIFIER_MAX}
                      autoFocus
                      {...form.register('identifier')}
                    />
                  </Field>
                  <Field label="Mật khẩu" required error={errors.password?.message}>
                    <Input
                      className="h-12 text-[15px]"
                      type={showPw ? 'text' : 'password'}
                      autoComplete="current-password"
                      enterKeyHint="go"
                      {...form.register('password')}
                      trailing={
                        <IconButton label={showPw ? 'Ẩn mật khẩu' : 'Hiện mật khẩu'} size="sm" onClick={() => setShowPw((v) => !v)}>
                          {showPw ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                        </IconButton>
                      }
                    />
                  </Field>

                  <Button type="submit" variant="primary" loading={isSubmitting} className="group mt-1 h-12 w-full text-[15px]">
                    Đăng nhập
                    <ArrowRight className="size-4 transition-transform duration-200 group-hover:translate-x-0.5" aria-hidden />
                  </Button>
                </>
              )}
            </div>

            <p className="anim-rise mt-8 text-[13px] leading-relaxed text-subtle" style={rise(4)}>
              Quên mật khẩu hoặc tài khoản bị khóa? Liên hệ quản trị hệ thống để được cấp lại.
            </p>
          </form>
        </div>

        <div className={cn(brandLoading && 'invisible')}>
          <Footer brand={brand} />
        </div>
      </main>
    </div>
  );
}

/** Step 2 of login when the same email/phone + password exists in several companies. */
function TenantPicker({
  identifier,
  choices,
  value,
  onChange,
  onBack,
  busy,
}: {
  identifier: string;
  choices: TenantChoice[];
  value: string;
  onChange: (code: string) => void;
  onBack: () => void;
  busy: boolean;
}) {
  return (
    <>
      <fieldset className="flex flex-col gap-3" aria-describedby="tenant-pick-hint">
        <legend className="mb-1 text-[15px] leading-snug font-semibold text-ink">
          Tài khoản thuộc nhiều doanh nghiệp — chọn doanh nghiệp
        </legend>
        <p id="tenant-pick-hint" className="text-[13px] text-muted">
          <span className="break-anywhere font-medium text-ink">{identifier}</span> có quyền truy cập các doanh nghiệp dưới đây.
        </p>
        <div className="flex flex-col divide-y divide-line overflow-hidden rounded-lg border border-line">
          {choices.map((c) => (
            <Radio
              key={c.code}
              id={`tenant-${c.code}`}
              name="tenant"
              value={c.code}
              checked={value === c.code}
              onChange={() => onChange(c.code)}
              className="w-full px-3.5 py-3 transition-colors hover:bg-surface-2 has-[:checked]:bg-accent-soft"
              label={
                <span className="flex items-center gap-2 font-medium">
                  <Building2 className="size-4 shrink-0 text-subtle" aria-hidden />
                  {c.name}
                </span>
              }
              description={<span className="font-mono">{c.code}</span>}
            />
          ))}
        </div>
      </fieldset>
      <Button type="submit" variant="primary" loading={busy} disabled={!value} className="group h-12 w-full text-[15px]">
        Tiếp tục
        <ArrowRight className="size-4 transition-transform duration-200 group-hover:translate-x-0.5" aria-hidden />
      </Button>
      <Button variant="ghost" onClick={onBack} disabled={busy} className="-mt-2 w-full" icon={<ArrowLeft className="size-4" aria-hidden />}>
        Dùng tài khoản khác
      </Button>
    </>
  );
}

function MobileBrand({ brand }: { brand: Brand }) {
  return (
    <div className="flex items-center gap-3">
      <BrandMark brand={brand} className="size-9 rounded-[10px] text-[13px] tracking-tight" />
      <div className="min-w-0 leading-tight">
        <p className="text-sm font-semibold text-ink">{brand.custom ? brand.name : 'Nguyên Bình'}</p>
        <p className="text-xs text-subtle">{brand.custom ? (brand.tagline ?? brand.companyName) : <>CRM · Đào tạo &amp; dịch vụ</>}</p>
      </div>
    </div>
  );
}

function Footer({ brand }: { brand: Brand }): ReactNode {
  if (!brand.custom) return <p className="mx-auto w-full max-w-[380px] text-xs text-subtle">Sản phẩm của Công ty CP Nguyên Bình Holdings</p>;
  return <p className="mx-auto w-full max-w-[380px] text-xs text-subtle">Sản phẩm của {brand.companyName}</p>;
}
